import axios, { AxiosResponse } from 'axios';
import queryString from 'query-string';
import 'url-search-params-polyfill';
import { apiUrl } from '@/env';
import { getLocalToken } from '@/utils';
import {
  IUserProfile,
  TableData,
  IDRec,
  APIResult,
  ChoiceOptionList,
  AutoCompleteList
} from '@/interfaces';
import {
  commitAddNotification,
  commitRemovePersistentNotifications
} from './store/main/mutations';

function axiosParams(token: string, params: object|null = null) {
  const cfg: any = {
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  if (params) {
    cfg.params = params;
  }
  return cfg;
}

function handleError(store, error, message = 'Er is een fout opgetreden') {
  // console.log('handleError', error, message);
  if (error.response.status === 403) {
    // Token expired
    sessionStorage.setItem(
      'redirectPath',
      window.location.pathname + window.location.search
    );
    window.location.href = '/login';
  } else {
    if (store) {
      // Conflict
      if (error.response.status === 409) {
        switch (error.response.data.detail) {
          case 'IntegrityError':
            commitAddNotification(store, {
              content:
                'De gegevens zijn nog in gebruik en kunnen niet verwijderd worden',
              color: 'warning'
            });
            break;
          case 'SerialNumberNotUnique':
            commitAddNotification(store, {
              content:
                'Dit serienummer is al in gebruik en kan niet gebruikt worden',
              color: 'warning'
            });
            break;
          case 'ReportCodeNotUnique':
            commitAddNotification(store, {
              content:
                'Dit dossier nummer is al in gebruik en kan niet gebruikt worden',
              color: 'warning'
            });
            break;
          default:
            commitAddNotification(store, {
              content: message,
              color: 'error'
            });
        }
      } else {
        commitAddNotification(store, {
          content: message,
          color: 'error'
        });
      }
    }
  }
  return null;
}

function getApiUrl(relUrl: string) {
  return `${apiUrl}/api/v1${relUrl}`;
}

function parseFileNameFromURL(urlStr: string): string | undefined {
  const url = new URL(urlStr);
  return url.pathname.split('/').pop();
}

function downloadFromResponse(response: AxiosResponse) {
  let fileName = '';
  const disposition = response.headers['content-disposition'];
  if (disposition && disposition.indexOf('attachment') !== -1) {
    // Get filename with content-disposition header
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(disposition);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }
  } else {
    const lastPart = parseFileNameFromURL(response.request.responseURL);
    if (lastPart) {
      fileName = lastPart;
    }
  }
  const blob = new Blob([response.data], {
    type: response.headers['content-type']
  });
  const fileURL = window.URL.createObjectURL(blob);
  const fileLink = document.createElement('a');
  document.body.appendChild(fileLink); // Add the element to the DOM; firefox fix
  (fileLink as any).style = 'display: none';
  fileLink.href = fileURL;
  fileLink.download = fileName;
  fileLink.click();
  window.URL.revokeObjectURL(fileLink.href);
}

export const api = {
  // parseFileNameFromURL = parseFileNameFromURL;
  async logInGetToken(username: string, password: string, otpcode: string) {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('otpcode', otpcode);

    return axios.post(`${apiUrl}/api/v1/login/access-token`, params, {
      validateStatus: status => {
        return true;
      }
    });
  },

  async getMe(token: string) {
    return axios.get<IUserProfile>(`${apiUrl}/api/v1/users/me`, axiosParams(token));
  },

  async passwordRecovery(login: string) {
    return axios.post<APIResult>(`${apiUrl}/api/v1/password-recovery/${login}`).then(
      response => {
        // if (store) {
        //   commitAddNotification(store, {
        //     content: 'Actie geslaagd',
        //     color: 'success'
        //   });
        // }
        return response.data;
      },
      error => {
        return handleError(null, error);
      }
    );

  },

  async resetPassword(password: string, token: string) {
    return axios.post<APIResult>(`${apiUrl}/api/v1/reset-password/`, {new_password: password, token}).then(
      response => {
        // if (store) {
        //   commitAddNotification(store, {
        //     content: 'Actie geslaagd',
        //     color: 'success'
        //   });
        // }
        return response.data;
      },
      error => {
        return handleError(null, error);
      }
    );
  },

  async getTableData(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl);
    if (queryObj !== {}) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get<TableData>(url, axiosParams(token)).then(
        response => {
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async getData(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl);
    if (queryObj !== {}) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get(url, axiosParams(token)).then(
        response => {
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async getChoices(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl);
    if (queryObj) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get<ChoiceOptionList>(url, axiosParams(token)).then(
        response => {
          return response.data;
        },
        error => {
          return handleError(
            store,
            error,
            'Er is een fout opgetreden bij het ophalen van een keuzelijst'
          );
        }
      );
    }
  },

  async getAutoCompleteList(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl);
    if (queryObj) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get<AutoCompleteList>(url, axiosParams(token)).then(
        response => {
          return response.data;
        },
        error => {
          return handleError(
            store,
            error,
            'Er is een fout opgetreden bij het ophalen van een keuzelijst'
          );
        }
      );
    }
  },

  async downloadFileWithLink(store, url, directDownload = true) {
    if (directDownload) {
      const fileLink = document.createElement('a');
      document.body.appendChild(fileLink); // Add the element to the DOM; firefox fix
      (fileLink as any).style = 'display: none';
      fileLink.href = url + '?for_download=1';
      fileLink.click();
      window.URL.revokeObjectURL(fileLink.href);
    } else {
      if (store) {
        commitAddNotification(store, {
          content: 'Downloaden...',
          showProgress: true,
          persistent: true,
          color: 'primary'
        });
      }
      axios({
        method: 'get',
        url,
        responseType: 'blob'
      }).then(
        response => {
          downloadFromResponse(response);
          commitRemovePersistentNotifications(store);
          // commitAddNotification(store, {
          //   content: 'Download geslaagd',
          //   color: 'success'
          // });
        },
        error => {
          commitRemovePersistentNotifications(store);
          return handleError(store, error, 'Er is een fout opgetreden');
        }
      );
    }
  },

  async downloadAction(store, relUrl: string, data: object = {}) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {

      return axios.post<string>(url, data, axiosParams(token)).then(
        response => {
          api.downloadFileWithLink(store, response.data);
        },
        error => {
          commitRemovePersistentNotifications(store);
          return handleError(store, error);
        }
      );
    }
  },

  async downloadFile(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl + '/url');
    if (queryObj) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
        return axios.get<string>(url, axiosParams(token)).then(
        response => {
          api.downloadFileWithLink(store, response.data);
        },
        error => {
          commitRemovePersistentNotifications(store);
          return handleError(store, error);
        }
      );
    }
  },

  async openFile(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl + '/url');
    if (queryObj) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get<string>(url, axiosParams(token)).then(
        response => {
          if (window) {
            const newWindow = window.open(response.data, '_blank');
            if (newWindow) {
              newWindow.focus();
            }
          }
        },
        error => {
          commitRemovePersistentNotifications(store);
          return handleError(store, error);
        }
      );
    }
  },

  async getFileURL(store, relUrl: string, dataurl: boolean = true) {
    const url = getApiUrl(relUrl + (dataurl ? '/dataurl' : '/url'));
    const token = getLocalToken();
    if (token) {
      return axios.get(url, axiosParams(token)).then(
        response => {
          return response.data;
        },
        error => {
          if (error.response.status === 404) {
            return '';
          } else {
            commitRemovePersistentNotifications(store);
            return handleError(store, error);
          }
        }
      );
    }
  },

  async callValidator(store, valtype: string, v: string) {
    const url = getApiUrl('/validators/' + valtype);
    return axios.get(url, { params: { v } }).then(
      response => {
        return response.data;
      },
      error => {
        return handleError(store, error);
      }
    );
  },

  async getRecord(store, relUrl: string, queryObj: object = {}) {
    let url = getApiUrl(relUrl);
    if (queryObj) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.get<IDRec>(url, axiosParams(token)).then(
        response => {
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async createRecord(store, relUrl: string, rec: object) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.post<IDRec>(url, rec, axiosParams(token)).then(
        response => {
          if (store) {
            commitAddNotification(store, {
              content: 'Aangemaakt',
              color: 'success'
            });
          }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async updateRecord(store, relUrl: string, rec: object) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.put<IDRec>(url, rec, axiosParams(token)).then(
        response => {
          if (store) {
            commitAddNotification(store, {
              content: 'Opgeslagen',
              color: 'success'
            });
          }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async deleteRecord(store, relUrl: string) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.delete(url, axiosParams(token)).then(
        response => {
          if (store) {
            commitAddNotification(store, {
              content: 'Verwijderd',
              color: 'success'
            });
          }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async duplicateRecord(
    store,
    relUrl: string,
    queryObj: object,
    data: object = {}
  ) {
    let url = getApiUrl(relUrl + '/duplicate');
    if (queryObj !== {}) {
      url += '?' + queryString.stringify(queryObj);
    }
    const token = getLocalToken();
    if (token) {
      return axios.post<IDRec>(url, data, axiosParams(token)).then(
        response => {
          // if (store) {
          //   commitAddNotification(store, {
          //     content: 'Actie geslaagd',
          //     color: 'success'
          //   });
          // }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async postAction(store, relUrl: string, data: object = {}, params: object|null = null) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.post<string>(url, data, axiosParams(token, params)).then(
        response => {
          // if (store) {
          //   commitAddNotification(store, {
          //     content: 'Actie geslaagd',
          //     color: 'success'
          //   });
          // }
          return response;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async postActionAPIResult(store, relUrl: string, data: object = {}) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.post<APIResult>(url, data, axiosParams(token)).then(
        response => {
          // if (store) {
          //   commitAddNotification(store, {
          //     content: 'Actie geslaagd',
          //     color: 'success'
          //   });
          // }
          return response.data;
        },
        error => {
          return handleError(store, error);
        }
      );
    }
  },

  async uploadFile(store, relUrl: string, data: object) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.post<IDRec>(url, data, axiosParams(token)).then(
        response => {
          // if (store) {
          //   commitAddNotification(store, {content: 'Upload geslaagd', color: 'success'});
          // }
          return response;
        },
        error => {
          return handleError(
            store,
            error,
            'Er is een fout opgetreden bij het uploaden van data'
          );
        }
      );
    }
  },

  async deleteFile(store, relUrl: string) {
    const url = getApiUrl(relUrl);
    const token = getLocalToken();
    if (token) {
      return axios.delete<IDRec>(url, axiosParams(token)).then(
        response => {
          // if (store) {
          //   commitAddNotification(store, {
          //     content: 'Delete geslaagd',
          //     color: 'success'
          //   });
          // }
          return response;
        },
        error => {
          return handleError(
            store,
            error,
            'Er is een fout opgetreden bij het deleten van een file'
          );
        }
      );
    }
  }
};
